import { useState, useEffect, useCallback } from "react";
import { auth, AuthError } from "@/lib/auth";
import type { User } from "@/types/cat";
import { isLinguaproz } from "@/config/mode";

interface UseAuthState {
    user: User | null;
    loading: boolean;
    error: string | null;
}

interface UseAuthActions {
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (
        email: string,
        password: string,
        metadata?: { full_name?: string },
    ) => Promise<void>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    updateProfile: (updates: {
        full_name?: string;
        avatar_url?: string;
    }) => Promise<void>;
    clearError: () => void;
    refreshUser: () => Promise<void>;
}

export function useAuth(): UseAuthState & UseAuthActions {
    const [state, setState] = useState<UseAuthState>({
        user: null,
        loading: true,
        error: null,
    });

    // Set loading state
    const setLoading = useCallback((loading: boolean) => {
        setState((prev) => ({ ...prev, loading }));
    }, []);

    // Set error state
    const setError = useCallback((error: string | null) => {
        setState((prev) => ({ ...prev, error, loading: false }));
    }, []);

    // Set user state
    const setUser = useCallback((user: User | null) => {
        setState((prev) => ({ ...prev, user, loading: false, error: null }));
    }, []);

    // Clear error
    const clearError = useCallback(() => {
        setState((prev) => ({ ...prev, error: null }));
    }, []);

    // Refresh user data
    const refreshUser = useCallback(async () => {
        try {
            setLoading(true);
            const user = await auth.getCurrentUser();
            setUser(user);
        } catch (error) {
            console.error("Error refreshing user:", error);
            setError("Failed to refresh user data");
        }
    }, [setLoading, setUser, setError]);

    // Sign in
    const signIn = useCallback(
        async (email: string, password: string) => {
            try {
                setLoading(true);
                clearError();
                const user = await auth.signIn(email, password);
                setUser(user);
                (window as any).posthog?.identify(user.id, {
                    email: user.email,
                    name: user.user_metadata?.full_name || user.email,
                });
            } catch (error) {
                if (error instanceof AuthError) {
                    setError(error.message);
                    throw error;
                } else {
                    setError("Failed to sign in");
                    throw new Error("Failed to sign in");
                }
            } finally {
                setLoading(false);
            }
        },
        [setLoading, setUser, setError, clearError],
    );

    // Sign up
    const signUp = useCallback(
        async (
            email: string,
            password: string,
            metadata?: { full_name?: string },
        ) => {
            try {
                setLoading(true);
                clearError();
                const user = await auth.signUp(email, password, {
                    ...metadata,
                    role: "translator", // Default role for CAT tool users
                });
                setUser(user);
            } catch (error) {
                // Rethrown so the caller can show a clear message and does
                // not carry on as if the account had been created.
                if (error instanceof AuthError) {
                    setError(error.message);
                    throw error;
                } else {
                    setError("Failed to create account");
                    throw new Error("Failed to create account");
                }
            }
        },
        [setLoading, setUser, setError, clearError],
    );

    // Sign out
    const signOut = useCallback(async () => {
        try {
            setLoading(true);
            await auth.signOut();
            setUser(null);
        } catch (error) {
            if (error instanceof AuthError) {
                setError(error.message);
            } else {
                setError("Failed to sign out");
            }
        }
    }, [setLoading, setUser, setError]);

    // Reset password
    const resetPassword = useCallback(
        async (email: string) => {
            try {
                setLoading(true);
                clearError();
                await auth.resetPassword(email);
                setLoading(false);
                // Don't set user here, just confirm email was sent
            } catch (error) {
                if (error instanceof AuthError) {
                    setError(error.message);
                    throw error;
                } else {
                    setError("Failed to send reset email");
                    throw new Error("Failed to send reset email");
                }
            }
        },
        [setLoading, setError, clearError],
    );

    // Update profile
    const updateProfile = useCallback(
        async (updates: { full_name?: string; avatar_url?: string }) => {
            try {
                setLoading(true);
                clearError();
                const user = await auth.updateProfile(updates);
                setUser(user);
            } catch (error) {
                if (error instanceof AuthError) {
                    setError(error.message);
                } else {
                    setError("Failed to update profile");
                }
            }
        },
        [setLoading, setUser, setError, clearError],
    );

    // Initialize auth state and listen for changes
    useEffect(() => {
        let mounted = true;

        // Get initial user
        const initializeAuth = async () => {
            if (isLinguaproz) { if (mounted) setUser(null); return; }
            try {
                const user = await auth.getCurrentUser();
                if (mounted) {
                    setUser(user);
                }
            } catch (error) {
                console.error("Error initializing auth:", error);
                if (mounted) {
                    setError("Failed to initialize authentication");
                }
            }
        };

        initializeAuth();

        if (isLinguaproz) return () => { mounted = false; };
        // Listen for auth state changes
        const {
            data: { subscription },
        } = auth.onAuthStateChange((user) => {
            if (mounted) {
                setUser(user);
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, [setUser, setError]);

    return {
        // State
        user: state.user,
        loading: state.loading,
        error: state.error,

        // Actions
        signIn,
        signUp,
        signOut,
        resetPassword,
        updateProfile,
        clearError,
        refreshUser,
    };
}

// Hook for checking if user has specific role
export function useAuthRole(requiredRole: string) {
    const { user } = useAuth();
    return user?.role === requiredRole;
}

// Hook for protecting routes
export function useRequireAuth() {
    const { user, loading } = useAuth();

    useEffect(() => {
        if (!loading && !user) {
            // Redirect to login or show auth modal
            // This can be customized based on your routing solution
            window.location.href = "/login";
        }
    }, [user, loading]);

    return { user, loading, isAuthenticated: !!user };
}

export default useAuth;
